import { useEffect, useRef, useState } from "react";
import { buscarParticipantes, PessoaParticipante } from "services/pre-inscricao.service";

interface BuscaParticipantesProps {
  /** Programa exigido do participante; `null` dispensa o filtro. */
  programa: string | null;
  /** Matrículas que não podem aparecer (cadastrante e já adicionados). */
  ignorar: string[];
  desabilitado: boolean;
  onAdicionar: (pessoa: PessoaParticipante) => void;
}

const TERMO_MINIMO = 3;

/** Busca de participantes na base de pessoas, com debounce e mínimo de 3 caracteres. */
export function BuscaParticipantes({ programa, ignorar, desabilitado, onAdicionar }: BuscaParticipantesProps) {
  const [termo, setTermo] = useState("");
  const [resultados, setResultados] = useState<PessoaParticipante[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [falhou, setFalhou] = useState(false);
  const [buscou, setBuscou] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const buscar = async (valor: string) => {
    if (valor.trim().length < TERMO_MINIMO) {
      setResultados([]);
      setBuscou(false);
      return;
    }
    setBuscando(true);
    setFalhou(false);
    try {
      setResultados(await buscarParticipantes(valor.trim(), programa));
    } catch (error) {
      console.error("[BuscaParticipantes] falha na busca", error);
      setResultados([]);
      setFalhou(true);
    } finally {
      setBuscando(false);
      setBuscou(true);
    }
  };

  const visiveis = resultados.filter((pessoa) => !ignorar.includes(pessoa._id));

  const adicionar = (pessoa: PessoaParticipante) => {
    onAdicionar(pessoa);
    setTermo("");
    setResultados([]);
    setBuscou(false);
  };

  return (
    <div className="relative">
      <label htmlFor="buscar-participante" className="sr-only">
        Buscar participante por nome ou matrícula
      </label>
      <input
        id="buscar-participante"
        type="text"
        role="combobox"
        aria-expanded={visiveis.length > 0}
        aria-controls="participantes-resultados"
        aria-autocomplete="list"
        value={termo}
        disabled={desabilitado}
        onChange={(e) => {
          const valor = e.target.value;
          setTermo(valor);
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => buscar(valor), 350);
        }}
        placeholder={desabilitado ? "Limite de participantes atingido" : "Digite o nome ou a matrícula (F1234567)"}
        className="w-full border border-gray-bb-4 rounded-lg p-2.5 sm:p-3 text-sm sm:text-base focus:ring-2 focus:ring-blue-bb focus:border-transparent transition-all disabled:bg-gray-100 disabled:cursor-not-allowed"
      />
      {buscando && (
        <div className="absolute right-3 top-3" role="status" aria-label="Buscando participantes">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-bb" />
        </div>
      )}

      {visiveis.length > 0 && (
        <div id="participantes-resultados" role="listbox" className="mt-2 border border-gray-bb-4 rounded-lg max-h-60 overflow-y-auto bg-white shadow-lg">
          {visiveis.map((pessoa) => (
            <button
              key={pessoa._id}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => adicionar(pessoa)}
              className="w-full text-left p-3 hover:bg-blue-50 border-b border-gray-bb-4 last:border-b-0 transition-colors"
            >
              <div className="font-medium text-gray-bb-1">
                {pessoa.nome_guerra || pessoa.nome}
                <span className="text-sm text-gray-bb-2 ml-2">({pessoa._id})</span>
              </div>
              <div className="text-sm text-gray-bb-2">
                {[pessoa.funcao?.nome, pessoa.lotacao?.nome].filter(Boolean).join(" · ")}
              </div>
            </button>
          ))}
        </div>
      )}

      {falhou && (
        <p className="mt-2 text-sm text-red-600" role="alert">
          Não foi possível buscar agora. Tente de novo em instantes.
        </p>
      )}
      {buscou && !buscando && !falhou && visiveis.length === 0 && (
        <p className="mt-2 p-3 text-gray-bb-2 text-sm bg-gray-50 rounded-lg" role="status">
          Nenhum funcionário elegível encontrado
        </p>
      )}
    </div>
  );
}
